import { FC, useState } from "react";
import { Form, Input } from "antd";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import dayjs, { Dayjs } from "dayjs";
import { PickerDay, PickerDayProps } from "@mui/x-date-pickers/PickerDay";
import AppDateCalendar from "@Components/AppDateCalendar/AppDateCalendar";
import { withFloatingLabel } from "src/HOC/WithFloatingLabel";
import Carrier from "@Components/Carrier/Carrier";
import { CalendarIcon } from "src/assets/Icons";
import { formatDate } from "src/utility/time";
import CheckInFieldContent from "../CheckInFieldContent/CheckInFieldContent";
import CheckOutFieldContent from "../CheckOutFieldContent/CheckOutFieldContent";

const FloatingInput = withFloatingLabel(Input);
const DATE_FORMAT = "ddd, DD MMM YYYY";

type RangeDayProps = PickerDayProps & { start?: Dayjs; end?: Dayjs };

const RangeDay: FC<RangeDayProps> = ({ day, start, end, outsideCurrentMonth, className, disabled, ...other }) => {
  const date = dayjs(day);
  const isStart = !!start && date.isSame(start, "day");
  const isEnd = !!end && date.isSame(end, "day");
  const inRange = !!start && !!end && date.isAfter(start, "day") && date.isBefore(end, "day");

  return (
    <PickerDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      selected={isStart || isEnd}
      disabled={disabled}
      className={cn(
        className,
        !outsideCurrentMonth && inRange && "bg-background-primary-light! rounded-none! text-font-active!",
        !outsideCurrentMonth && isStart && !!end && "rounded-e-none!",
        !outsideCurrentMonth && isEnd && !!start && "rounded-s-none!",
        !outsideCurrentMonth && disabled && "text-font-disabled! cursor-not-allowed!",
      )}
    />
  );
};

const CheckInOutComponent: FC<{ errorFields: string[] }> = ({ errorFields }) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();

  const checkin: Dayjs | undefined = Form.useWatch("checkin", form);
  const checkout: Dayjs | undefined = Form.useWatch("checkout", form);
  const hasError = errorFields.includes("checkin") || errorFields.includes("checkout");

  const [month, setMonth] = useState<Dayjs>(checkin ?? dayjs());

  const label = t("Hotels.common.dates");
  const displayValue = [formatDate(checkin, DATE_FORMAT), formatDate(checkout, DATE_FORMAT)].filter(Boolean).join("  -  ");

  const handleSelect = (date: Dayjs | null) => {
    if (!date) return;

    if (!checkin || checkout) {
      form.setFieldsValue({ checkin: date, checkout: undefined });
      return;
    }

    if (date.isSame(checkin, "day")) return;

    form.setFieldsValue({ checkout: date });
  };

  const isDateDisabled = (date: Dayjs) => {
    if (!checkin || checkout) return false;
    return date.isBefore(checkin, "day") || date.isAfter(checkin.add(1, "month"), "day");
  };

  return Carrier({
    label,
    renderTrigger: ({ open }) => (
      <div className={`border-b border-border-default flex w-full flex-1 items-center gap-1 px-1 ${hasError ? "text-font-danger" : "text-icon-minimal"}`}>
        <CalendarIcon className="h-4! w-4!" />
        <FloatingInput
          label={label}
          readOnly
          value={displayValue}
          onClick={open}
          className="text-font-default! border-none! outline-none! font-semibold shadow-none! pt-5! pb-1! px-3! w-full cursor-pointer"
        />
      </div>
    ),
    renderSheet: () => (
      <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
        <div className="flex items-stretch gap-2">
          <CheckInFieldContent className="w-full m-0!" getValueProps={(value: Dayjs | undefined) => ({ value: formatDate(value, DATE_FORMAT) })}>
            <FloatingInput
              label={t("Hotels.common.checkIn")}
              disabled
              className="text-font-default! border-none! outline-none! font-semibold shadow-none! pt-5! pb-1! px-3! w-full bg-background-disabled! rounded-md"
            />
          </CheckInFieldContent>
          <CheckOutFieldContent className="w-full m-0!" getValueProps={(value: Dayjs | undefined) => ({ value: formatDate(value, DATE_FORMAT) })}>
            <FloatingInput
              label={t("Hotels.common.checkOut")}
              disabled
              className="text-font-default! border-none! outline-none! font-semibold shadow-none! pt-5! pb-1! px-3! w-full bg-background-disabled! rounded-md"
            />
          </CheckOutFieldContent>
        </div>

        <AppDateCalendar
          value={month}
          onChange={handleSelect}
          onMonthChange={setMonth}
          disablePast
          shouldDisableDate={isDateDisabled}
          slots={{ day: RangeDay }}
          slotProps={{ day: { start: checkin, end: checkout } as any }}
        />
      </div>
    ),
  });
};

export default CheckInOutComponent;
